/**
 * An item's Analytics tab (the item editor's third tab): its totals against the previous period,
 * its plays day by day with the previous period behind them, "How far people watch" for anything
 * with a duration, and live against replay for a live event. Its own range picker (the same days
 * as Analytics, kept in the URL).
 */

import { SkeletonCard } from "@web-ui/components/Skeleton";
import { TileGrid } from "../components/tile-grid";
import type { EntityType } from "@/api/admin";
import { go, type Route } from "@/lib/route";
import { analytics, type ItemReport } from "../api";
import { BarList } from "../charts/bar-list";
import { RetentionCurve } from "../charts/retention-curve";
import type { Table } from "../csv";
import { definition, modeLabel, type MetricId } from "../definitions";
import { changeOf, formatFigure, totalsFigure } from "../figures";
import { count, duration, percent } from "../format";
import { MetricTile } from "../components/metric-tile";
import { NoData, ReportFailed } from "../components/no-data";
import { RangePicker } from "../components/range-picker";
import { ReportCard } from "../components/report-card";
import { SampleNotice } from "../components/sample-notice";
import { TrendCard } from "../components/trend-card";
import { parseRange } from "../range";
import { useReportScope, type ReportScope } from "../scope";
import { useReport } from "../use-report";

const TILES: MetricId[] = ["plays", "watchSeconds", "viewers", "averageWatch", "engagedRate", "completionRate"];

export function ItemAnalytics({ route, type, name, durationSeconds }: { route: Route; type: EntityType; name: string; durationSeconds?: number }) {
  const days = parseRange(route.range);
  const scope = useReportScope(days);
  const id = route.id!;
  const now = useReport(() => analytics.item(scope.client, id, scope.range), [scope.client, id, scope.range]);
  const before = useReport(() => analytics.item(scope.client, id, scope.previous), [scope.client, id, scope.previous]);
  const r = now.data;
  const isEvent = type === "liveEvent";

  return (
    <div className="flex flex-col gap-3">
      <RangePicker value={days} onChange={(next) => go({ ...route, tab: "analytics", range: next })} />
      {now.failed && !r ? <section className="card"><ReportFailed /></section> : !r ? <SkeletonCard label="Loading its numbers" rows={6} /> : (
        <div className={`flex flex-col gap-3 transition-opacity ${now.stale ? "opacity-60" : ""}`} aria-busy={now.stale || undefined}>
          <SampleNotice shown={r.sample || !!before.data?.sample} />
          {r.totals.plays === 0 && r.previousTotals.plays === 0 && !r.sample ? (
            <section className="card"><NoData why={`Numbers appear once viewers watch “${name}” in your apps. It needs to be in the app first: show it and publish.`} /></section>
          ) : (
            <>
              <TileGrid columns={3}>
                {TILES.map((tile) => {
                  const def = definition(tile, scope.pack);
                  const value = totalsFigure(tile, r.totals);
                  return <MetricTile key={tile} definition={def} value={formatFigure(tile, value)} change={changeOf(tile, value, totalsFigure(tile, r.previousTotals))} />;
                })}
              </TileGrid>
              <TrendCard scope={scope} current={r.series} before={before.data?.series ?? null} what={name} empty={r.totals.plays === 0} />
              {!isEvent && r.retention.some((share) => share > 0) ? <RetentionCard scope={scope} r={r} name={name} durationSeconds={durationSeconds} /> : null}
              {isEvent ? <ModesCard scope={scope} r={r} name={name} /> : null}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function RetentionCard({ scope, r, name, durationSeconds }: { scope: ReportScope; r: ItemReport; name: string; durationSeconds?: number }) {
  const def = definition("retention", scope.pack);
  const rows = r.retention.map((share, i) => ({ i, share }));
  const table: Table<{ i: number; share: number }> = {
    columns: [
      { label: "Part of the video", value: (p) => `${p.i * 5}–${p.i * 5 + 5}%` },
      ...(durationSeconds ? [{ label: "From", csvLabel: "From (seconds)", value: (p: { i: number }) => Math.round((p.i / 20) * durationSeconds), format: (v: unknown) => duration(Number(v)) }] : []),
      { label: "Still watching", csvLabel: "Share of plays (0-1)", value: (p) => Number(p.share.toFixed(4)), format: (v) => percent(Number(v)), numeric: true },
    ],
    rows,
  };
  return (
    <ReportCard title={def} description="Of everyone who pressed play, how many watched each part. A steep drop shows where people leave." table={table}
      file={{ brand: scope.brand, what: `${name} how far people watch`, range: scope.range }}>
      <RetentionCurve retention={r.retention} durationSeconds={durationSeconds} title={`${def.label}: ${name}`} />
    </ReportCard>
  );
}

function ModesCard({ scope, r, name }: { scope: ReportScope; r: ItemReport; name: string }) {
  const modes = [...r.modes].sort((a, b) => b.watchSeconds - a.watchSeconds);
  const table: Table<ItemReport["modes"][number]> = {
    columns: [
      { label: "Watched", value: (m) => modeLabel(m.mode) },
      { label: "Plays", value: (m) => m.plays, format: (v) => count(Number(v)), numeric: true },
      { label: "Watch time", csvLabel: "Watch time (seconds)", value: (m) => m.watchSeconds, format: (v) => duration(Number(v)), numeric: true },
    ],
    rows: modes,
  };
  return (
    <ReportCard title="Live and replay" description="How it was watched: while it was live, or afterwards as a replay. Together they are the whole attendance." table={table}
      file={{ brand: scope.brand, what: `${name} live and replay`, range: scope.range }}
      empty={modes.length === 0 ? <NoData title="Nobody has watched it yet" /> : undefined}>
      <BarList label="Watch time by how it was watched" bars={modes.map((m) => ({ key: m.mode, label: modeLabel(m.mode), value: m.watchSeconds, text: duration(m.watchSeconds), detail: `${count(m.plays)} ${m.plays === 1 ? "play" : "plays"}` }))} />
    </ReportCard>
  );
}
